/**
 * Helper: Format
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 * - Add in alphabetical order.
 *
 */

import { decodeApiId, transformEdges } from '~/helpers/graphql'
import { applyConversionRate } from '~/helpers/general'

/**
 * Formats price.
 * @param {Object} price - Price response.
 * @param {Boolean} convert - Apply shopify conversion rate.
 * @returns {Object}
 */
function formatPrice(price, convert = false) {
  let amount = (Number(price?.minVariantPrice.amount) * 100) || false
  let max = (Number(price?.maxVariantPrice.amount) * 100) || false
  let min = (Number(price?.minVariantPrice.amount) * 100) || false

  if (convert) {
    amount = applyConversionRate(amount)
    max = applyConversionRate(max)
    min = applyConversionRate(min)
  }

  return {
    amount,
    currencyCode: price?.minVariantPrice.currencyCode,
    max,
    min,
    varies: price?.maxVariantPrice.amount !== price?.minVariantPrice.amount,
  }
}

/**
 * Formats images.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatImages(product) {
  return transformEdges(product.images).map((image) => {
    const linkedVariant = transformEdges(product.variants).find((variant) => {
      return variant.image.id === image.id
    })

    return {
      alt: image.altText,
      height: image.height,
      id: decodeApiId(image.id),
      src: image.originalSrc,
      variant: decodeApiId(linkedVariant?.id) || false,
      width: image.width,
    }
  })
}

/**
 * Formats variants for bulk ordering module.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatBulkOrderVariants(product) {
  const standardVariants = transformEdges(product.variants).filter((variant) => {
    return variant.title.includes('Standard') && variant.title.includes('Medium')
  })

  return standardVariants.map((variant) => {
    const formattedVariant = {
      available: variant.available,
      compareAtPrice: {
        amount: (Number(variant.compareAtPriceV2?.amount) * 100) || false,
        currencyCode: variant.compareAtPriceV2?.currencyCode || false,
      },
      id: decodeApiId(variant.id),
      image: decodeApiId(variant.image?.id) || false,
      originalSrc: variant.image.originalSrc,
      inventory: variant.quantityAvailable,
      price: {
        amount: Number(variant.priceV2.amount) * 100,
        currencyCode: variant.priceV2.currencyCode,
      },
      productTitle: variant.product.title,
      sku: variant.sku,
      selectedOptions: variant.selectedOptions.map((option) => {
        return {
          name: option.name,
          value: option.value,
        }
      }),
      title: variant.title,
    }

    return formattedVariant
  })
}

/**
 * Formats response from bulk ordering collection.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatBulkOrderCollection(response) {
  const collection = response.collection

  const formattedCollection = {
    description: collection.descriptionHtml,
    handle: collection.handle,
    id: decodeApiId(collection.id),
    title: collection.title,
    url: `//${location.host}/collections/${collection.handle}`,
  }

  /**
   * Format products.
   */
  formattedCollection.products = transformEdges(collection.products).map((productResponse) => {
    const product = productResponse
    const images = formatImages(product)
    const featuredImage = images[0]
    const compareAtPrice = formatPrice(product.compareAtPriceRange)
    const price = formatPrice(product.priceRange)
    const url = `//${location.host}/products/${product.handle}`
    const variants = formatBulkOrderVariants(product)

    const {
      available,
      handle,
      id,
      tags,
      title,
    } = product

    return {
      available,
      compareAtPrice,
      featuredImage,
      handle,
      id,
      images,
      price,
      tags,
      title,
      url,
      variants,
    }
  })

  return formattedCollection
}
